import React, { FC } from "react";
import { Box, Grid, Stack, SxProps, Typography } from "@mui/material";

import ServiceIcon from "../ServiceIcon";
import { CardGridProps, CardProps } from "./types";
import Markdown from "../Markdown";
import BaseLink from "../Link";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";

interface Props {
  header: string;
  cards: CardProps[];
}

const Card: FC<CardProps> = ({ title, category, icon, content, cta }) => {
  const ctaLabel: string = cta?.label ? cta.label : "Read more";
  const iconSize: number =
    icon?.variant === "icon-only" ? 50 : icon?.size ? icon?.size : 24;
  const iconStyle: SxProps =
    icon?.variant === "icon-only"
      ? {}
      : { backgroundColor: "#ECEFF1", borderRadius: "10px" };

  return (
    <Stack justifyContent="space-between" height="100%">
      <Stack>
        {icon && (
          <Stack
            alignItems="center"
            justifyContent="center"
            mb="20px"
            sx={{
              width: "50px",
              height: "50px",
              color: Colors.PrimaryMain,
              ...iconStyle,
            }}
          >
            <ServiceIcon
              service={icon.name}
              size={iconSize}
              strokeWidth={icon?.strokeWidth}
              strokeColor={icon?.strokeColor ? icon?.strokeColor : "#FFF"}
            />
          </Stack>
        )}
        {category && (
          <Typography variant="caption" sx={{ marginBottom: "16px" }}>
            {category}
          </Typography>
        )}
        <Typography variant="h5" sx={{ fontSize: "20px", lineHeight: "24px" }}>
          {title}
        </Typography>
        {content && (
          <Typography
            variant="body2"
            sx={{
              fontSize: "16px",
              fontWeight: "300",
              color: Colors.HeadingText,
              "> p:last-child": { mb: "0px" },
            }}
            component="div"
          >
            <Markdown content={content} />
          </Typography>
        )}
      </Stack>
      {cta?.url && (
        <BaseLink
          url={cta.url}
          sx={{
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
            },
          }}
        >
          {ctaLabel}
        </BaseLink>
      )}
    </Stack>
  );
};

function getBackgroundColor(cardStyle: "light" | "dark" | "transparent") {
  switch (cardStyle) {
    case "dark":
      return Colors.SecondaryLight;

    case "transparent":
      return "transparent";

    default:
    case "light":
      return Colors.PrimaryContrast;
  }
}

const CardGrid: FC<CardGridProps> = ({
  header,
  cards,
  headerPaddingX = "0px",
  headerAlign = "center",
  cardStyle = "light",
  horizontalPadding = false,
}) => {
  const cardBackgroundStyle = getBackgroundColor(cardStyle);

  return (
    <Stack
      mt={5}
      mb={5}
      gap={3}
      textAlign={headerAlign}
      sx={{
        paddingX: horizontalPadding ? "48px" : "",
      }}
    >
      {header && (
        <Typography
          variant="h2"
          sx={{
            px: {
              xs: "0px",
              lg: headerPaddingX,
            },
          }}
          color={Colors.HeadingText}
        >
          {header}
        </Typography>
      )}
      <Grid container spacing={2.5} justifyContent="center" textAlign="left">
        {cards.map((card: CardProps, idx: number) => {
          return (
            <Grid
              item
              key={`card-${idx}`}
              xs={12}
              sm={6}
              md={cards.length === 4 ? 3 : 4}
            >
              <Box
                sx={{
                  width: "100%",
                  minHeight: "242px",
                  height: "100%",
                  padding: 3,
                  borderRadius: "15px",
                  backgroundColor: cardBackgroundStyle,
                }}
              >
                <Card key={`card-${idx}`} {...card} />
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};

export default CardGrid;
